import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=anon, object=auth
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(false))
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data?.access_token) localStorage.setItem("chkr_token", data.access_token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    localStorage.removeItem("chkr_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export const PERMS = {
  super_admin: 100,
  admin_empresa: 80,
  rh: 70,
  supervisor: 60,
  jefe_area: 50,
  auditor: 40,
  solo_lectura: 30,
  empleado: 20,
};

export const ROLE_LABELS = {
  super_admin: "Super Admin",
  admin_empresa: "Admin Empresa",
  rh: "Recursos Humanos",
  supervisor: "Supervisor",
  jefe_area: "Jefe de Área",
  empleado: "Empleado",
  auditor: "Auditor",
  solo_lectura: "Solo Lectura",
};

export function hasMinRole(user, role) {
  if (!user) return false;
  return (PERMS[user.role] || 0) >= (PERMS[role] || 0);
}

export function canManage(user) {
  return ["super_admin", "admin_empresa", "rh"].includes(user?.role);
}

export function canSupervise(user) {
  return ["super_admin", "admin_empresa", "rh", "supervisor", "jefe_area"].includes(user?.role);
}

// Single source of truth for per-page access, consumed by both the sidebar
// (to hide links) and the router (to block direct navigation by URL).
// `ctx` viene de useEmpresa(): las páginas de escuela solo existen si esEscuela.
export const ROUTE_ACCESS = {
  dashboard: (u) => u?.role !== "empleado",
  portal: (u) => u?.role === "empleado",
  empresas: (u) => hasMinRole(u, "admin_empresa") || u?.role === "auditor",
  centros: (u) => hasMinRole(u, "jefe_area") || u?.role === "auditor" || u?.role === "solo_lectura",
  empleados: (u) => hasMinRole(u, "jefe_area") || u?.role === "auditor" || u?.role === "solo_lectura",
  horarios: (u) => hasMinRole(u, "jefe_area") || u?.role === "auditor" || u?.role === "solo_lectura",
  // Los docentes de una escuela no tienen jornada: su asistencia es por clase (Mi Portal).
  jornadas: (u) => !(u?.empresa_tipo === "escuela" && u?.tipo_personal === "docente"),
  incidencias: () => true,
  perfil: () => true,
  checadores: (u) => canManage(u),
  reportes: (u) => u?.role !== "empleado",
  configuracion: (u) => canManage(u),
  calendario: (u) => u?.role !== "empleado",
  auditoria: (u) => u?.role !== "empleado",
  usuarios: (u) => canManage(u),
  suscripcion: (u) => canManage(u),
  escuela: (u, ctx) => canManage(u) && !!ctx?.esEscuela,
  cargaDocente: (u, ctx) => u?.role !== "empleado" && !!ctx?.esEscuela,
  asistenciaDocente: (u, ctx) => u?.role !== "empleado" && !!ctx?.esEscuela,
};
